import React from "react";
import { useEffect, useState } from "react";
import "./Rechner.scss";
import CurrencyInput, { formatValue } from "react-currency-input-field";
import TextTransition, { presets } from "react-text-transition";
import type { TextTransitionProps } from "react-text-transition/src/components/TextTransition";
// @ts-expect-error
import Awards_EC_Firmenradl_AT from "../assets/@2x_Awards_EC_Firmenradl_AT_2024.png";
// @ts-expect-error
import icon_arrow_down from "../assets/ic_arrow_down.svg";
import { uppercaseFR } from "../helpers/convertToUppercaseIfContainsFirmenradl";
import { getEndpoint } from "../helpers/getBaseUrl";
import { getRegisterUrl } from "../helpers/getRegisterUrl";
import { formatCurrency, formatPercent } from "../helpers/formatNumbers";
import { useCachedData } from "../hooks/useCachedData";
import useCalculateParams, { } from "../hooks/useCalculateParams";
import EEndpoints from "../types/enum/endpoints";
import type { ICalculatorInput, ILeasing } from "../types/types";
import Image from "./Image";
import RechnerModal from "./RechnerModal";
import Tooltip from "./Tooltip";
import { beginLaufzeit } from "../data/static_data";

let timer: NodeJS.Timeout | string | number | undefined;

function initialCalculationParams(
	insuranceType: string,
	leasingDuration: string,
	residualValue: string,
): ICalculatorInput {
	return {
		Einkommen: 2500,
		FahrradPreis: 3200,
		Versicherung: Number(insuranceType),
		Laufzeit: Number(leasingDuration),
		Vorsteuerabzug: true,
		SvAbzug: true,
		Restwertvariante: Number(residualValue),
		Pendlerpauschale: {
			Id: 0,
			Km: 50,
		},
		Familienbonus: {
			Id: 0,
			KinderBis17: 0,
			KinderAb18: 0,
		},
	};
}

type Props = {
	className?: string;
	internal: boolean;
};

function Rechner({ className, internal = false }: Props) {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState<string | "">("");
	const { insurances, leasingDurations, residualValues } = useCachedData();

	const [priceDirection, setPriceDirection] =
		useState<TextTransitionProps["direction"]>("down");
	const [discountDirection, setDiscountDirection] =
		useState<TextTransitionProps["direction"]>("down");

	const { form, setForm, calculationParams } = useCalculateParams({
		insuranceType: insurances[0].Id,
		residualValue: residualValues[0].Id,
		leasingDuration: beginLaufzeit,
		einkommen: 2500,
		price: 3200,
		vorsteuerabzug: true,
		pendlerpauschaleActive: false,
		pendlerpauschale: 0,
		familienbonusActive: false,
		fbBis17: 0,
		fbAb18: 0,
	});

	const [advancedMode, setAdvancedMode] = useState<boolean>(true);

	type ICalculator = ILeasing & { calculationParams: ICalculatorInput } & {
		gesamtPreis: number | string;
	};

	// inital rendering needs to be with strings otherwise the animation is wrong at the first content paint
	const [calculator, setCalculator] = useState<ICalculator>({
		MonatlicheZahlung: "113.43 €", // 113.42911216806851
		RateNetto: 197.78, // 197.780530711002
		RateBrutto: 237.34, // 237.33663685320238
		ErsparnisInEuro: "1.526,85 €", // 1526.8513079663558
		ErsparnisInProzent: 31, // 30.54313478628437
		Restwert: "749.85 €", // 749.85,
		RestwertInProzent: 15,
		MonatlicheZahlungAG: 197.78, // 197.780530711002
		RGG: 83.75, // 83.74869397033895
		Versicherung: 36,
		SteuerpflichtBetrag: 838.73, // 838.7252214
		Error: null,
		calculationParams: initialCalculationParams(
			form.insuranceType,
			"36",
			form.residualValue,
		),
		gesamtPreis: "3.472,15 €", // 3472.15, // 3240.53
	});

	useEffect(() => {
		if (form.familienbonusActive && !form.pendlerpauschaleActive) {
			setForm((form) => ({ ...form, pendlerpauschale: form.pendlerpauschale }));
		}
	}, [form.familienbonusActive]);

	useEffect(() => {
		if (
			!calculator.calculationParams ||
			// @ts-ignore TODO: fix this
			calculator.calculationParams !== calculationParams
		) {
			if (timer) {
				clearTimeout(timer);
			}

			timer = setTimeout(async () => {
				try {
					const response = await fetch(
						getEndpoint(EEndpoints.calculateLeasing),
						{
							method: "POST",
							headers: {
								Accept: "text/plain",
								"Content-Type": "application/json",
							},
							body: calculationParams,
						},
					);

					if (!response.ok) {
						let errorText = await response.text();
						if (errorText.includes('Der Fahrradpreis muss zwischen')) {
							const sentenceToReplace = 'Der Fahrradpreis muss zwischen 750 Euro und 15000 Euro liegen';
							errorText = sentenceToReplace;
						}
						if (
							errorText.includes("$.FahrradPreis") || // field empty Fahrrad-Preis
							errorText.includes("$.Einkommen") || // field empty Einkommen
							errorText.includes("klein (Id=1)") || // field Pendlerpauschale unter 19 KM
							errorText.includes("$.Familienbonus.KinderBis17") || // field empty Kinder bis 17 Jahre
							errorText.includes("$.Familienbonus.KinderAb18") || // field empty Kinder ab 18 Jahre
							errorText.includes("$.Pendlerpauschale.Km") // field empty Pendlerpauschale

						) {
							setError("");
						} else {
							setError(errorText);
						}
					}

					const data: ILeasing = await response.json();
					setError("");

					if (JSON.stringify(data) !== JSON.stringify(calculator)) {
						if (response.status === 200) {
							if (calculator.ErsparnisInEuro > data.ErsparnisInEuro) {
								setDiscountDirection("up");
								setPriceDirection("down");
							} else {
								setDiscountDirection("down");
								setPriceDirection("up");
							}

							const paramsAsObject = JSON.parse(calculationParams);

							setCalculator({
								...data,
								RestwertInProzent: formatPercent(data.RestwertInProzent),
								ErsparnisInEuro: formatCurrency(data.ErsparnisInEuro),
								ErsparnisInProzent: formatPercent(data.ErsparnisInProzent),
								// @ts-ignore TODO fix this
								calculationParams: calculationParams,
								gesamtPreis: formatCurrency(
									Number(data.Restwert) +
									Number(data.MonatlicheZahlung) *
									Number(paramsAsObject.Laufzeit),
								),
								MonatlicheZahlung: formatCurrency(data.MonatlicheZahlung),
								MonatlicheZahlungAG: formatCurrency(data.MonatlicheZahlungAG),
								Restwert: formatCurrency(data.Restwert),
							});
						}
					}
				} catch (err) {
					console.error("Error:", err);
				} finally {
					clearTimeout(timer);
					timer = undefined;
				}
			}, 300);
		}
	}, [
		calculator,
		calculationParams,
		setCalculator,
		setDiscountDirection,
		setPriceDirection,
	]);

	return (
		<>
			{open && <RechnerModal setModalVersicherungOpen={setOpen} />}
			<form aria-label="FIRMENRADL Calculator Form" className={`Rechner ${className}`}>
				<div className="Rechner-Content">
					<div className="Rechner-HeroImage">
						<HeroImage />
					</div>
					{/* {!internal && (
						<div className="Rechner-Awards">
							<img
								width={132}
								height={114}
								src={Awards_EC_Firmenradl_AT}
								role="presentation"
								alt="Hol dir dein FIRMENRADL!"
							/>
							<Image
								src="assets/Awards_EC_Firmenradl_AT.png"
								width={128}
								height={128}
								crop={false}
								// @ts-expect-error
								alt="Hol dir dein FIRMENRADL!"
							    role="presentation"
								visibleByDefault
								fetchpriority={"high"}
							/>
						</div>
					)} */}
					<div className="Rechner-Slider">
						<div className="Bubbles desktop-only">
							<div className={"Bubble Big"}>
								<small>bis zu</small>
								<TextTransition
									springConfig={presets.wobbly}
									className="textTransition"
									direction={discountDirection}
								>
									{calculator.ErsparnisInEuro}
								</TextTransition>
								<small>sparen</small>
							</div>
							<div className="Bubble Small">
								<small>bis zu</small>
								<TextTransition
									springConfig={presets.wobbly}
									className="textTransition"
									direction={discountDirection}
								>
									{calculator.ErsparnisInProzent}
								</TextTransition>
								<small>günstiger</small>
							</div>
						</div>
					</div>
					<div className="Rechner-Form">
						<h1>
							Ersparnis<span className="outline">Rechner</span>
						</h1>

						<div className="row">
							<div className="col">
								<p className="subline">
									Mit FIRMENRADL kannst du dir ein Fahrrad deiner Wahl stark
									vergünstigt leasen, rechne dir gleich jetzt die Ersparnis aus
									und radel los!
								</p>
							</div>
						</div>
						<div className="row main-row">
							<div className="col Tooltip">
								<div style={{ position: 'relative' }}>
									<label htmlFor="fahrradPreis" style={{ display: 'block', textAlign: 'left' }}>
										Fahrrad-Preis
										<span> *</span>
									</label>
									<span style={{
										position: 'absolute',
										top: 0,
										right: 0,
										fontSize: '16px',
										color: "white",
										fontWeight: 'bold'
									}}>€</span>
									<CurrencyInput
										id="fahrradPreis"
										name="fahrradPreis"
										required
										aria-required="true"
										aria-labelledby="fahrrad-preis"
										placeholder="Preis in EURO"
										value={form.price}
										decimalsLimit={2}
										allowNegativeValue={false}
										intlConfig={{ locale: "de-DE"/*, currency: "EUR"*/ }}
										groupSeparator="."
										className="currency"
										onValueChange={(value, name, values) => {
											let newValue = values?.float ?? 0;

											if (newValue > 15000) {
												newValue = 15000;
											}

											setForm((form) => ({
												...form,
												price: newValue,
											}));
										}}
									/>
								</div>
								<Tooltip>
									Gib hier bitte den Fahrrad-Preis inkl. Zubehör und Steuer ein
								</Tooltip>
							</div>
							<div className="col Tooltip">
								<div style={{ position: 'relative' }}>
									<label htmlFor="einkommen" style={{ display: 'block', textAlign: 'left' }}>Bruttogehalt / Monat
										<span> *</span>
									</label>
									<span style={{
										position: 'absolute',
										top: 0,
										right: 0,
										color: "white",
										fontSize: '16px',
										fontWeight: 'bold'
									}}>€</span>
									<CurrencyInput
										id="einkommen"
										name="einkommen"
										required
										aria-required="true"
										aria-labelledby="einkommen"
										placeholder="Einkommen in EURO"
										value={form.einkommen}
										decimalsLimit={2}
										allowNegativeValue={false}
										intlConfig={{ locale: "de-DE" /*, currency: "EUR"*/ }}
										groupSeparator="."
										className="currency"
										onValueChange={(value, name, values) => {

											let newValue = values?.float ?? 0;
											
											if (newValue > 15000) {
												newValue = 15000;
											}
											
											setForm((form) => ({
												...form,
												// biome-ignore lint/style/noNonNullAssertion: <explanation>
												einkommen: newValue,
											}))
										}
									}
									/>
								</div>
								<Tooltip>
									Gib hier bitte dein tatsächliches Bruttogehalt pro Monat ein
								</Tooltip>
							</div>
						</div>

						{error ? (
							<div className="row" style={{ color: 'white', padding: 0 }}>
								<div className="col" style={{ textAlign: 'left' }}>
									<span>{error}</span>
								</div>
							</div>
						) : null}

						<div className="row">
							<div className="col radioGroupContainer Tooltip" role="group" aria-label="Laufzeit">
								{/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
								<label>Laufzeit</label>
								<div className="radioGroup">
									{leasingDurations.map((i) => (
										<Tab
											key={i.Id}
											id={i.Id}
											value={form.leasingDuration}
											name="laufzeit"
											aria-labelledby="laufzeit-label"
											// @ts-expect-error
											optionChange={(e) =>
												setForm((form) => ({
													...form,
													leasingDuration: e.target.value,
												}))
											}
											label={`${i.Id} Monate`}
										/>
									))}
								</div>
								<Tooltip>
									Wähle hier bitte deine gewünschte Laufzeit.
									<br />
									Vergewissere dich aber auch ob in
									<br />
									deinem Unternehmen die Laufzeit möglich ist.
								</Tooltip>
							</div>
						</div>
						{!advancedMode && (
							<div
								className="extensionToggle"
								onClick={() => setAdvancedMode(!advancedMode)}
								onKeyDown={(e) =>
									e.key === "escape" && setAdvancedMode(!advancedMode)
								}
							>
								<img
									src={icon_arrow_down}
									alt="Weitere Optionen anzeigen"
									className={advancedMode ? "rotated" : ""}
								/>
								Erweiterte Berechnung anzeigen
								<img
									src={icon_arrow_down}
									alt="Weitere Optionen anzeigen"
									className={advancedMode ? "rotated" : ""}
								/>
							</div>
						)}

						{advancedMode && (
							<div className={"extendedOptions"}>
								<div className="radioGroupContainer Tooltip">
									<Tooltip>
										Wähle hier dein gewünschtes Versicherungspaket.
										<br />
										Die Inhalte findest du unter weitere Infos.
										<br />
										Vergewissere dich auch hier wie die Vorgaben in deinem
										Unternehmen sind.
									</Tooltip>
									<div className="labelContainer">
										{/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
										<label>Versicherungspaket</label>
										<button onClick={() => setOpen(true)} type="button" aria-labelledby="versicherung-label">
											Weitere Infos
										</button>
									</div>
									<div className="radioGroup">
										{insurances.map((i) => (
											<Tab
												key={i.Id}
												id={i.Id}
												value={form.insuranceType}
												name="versicherung"
												aria-labelledby="versicherung-label"
												// @ts-expect-error
												optionChange={(e) =>
													setForm((form) => ({
														...form,
														insuranceType: e.target.value,
													}))
												}
												label={uppercaseFR(i.Name)}
											/>
										))}
									</div>
								</div>

								<div className="radioGroupContainer Tooltip" role="group" aria-label="Restwert">
									<div className="labelContainer">
										{/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
										<label>Restwert</label>
									</div>
									<div className="radioGroup">
										{residualValues.map((i) => (
											<Tab
												key={i.Id}
												id={i.Id}
												value={form.residualValue}
												name="valueWVO"
												aria-labelledby="restwert-label"
												// @ts-expect-error
												optionChange={(e) =>
													setForm((form) => ({
														...form,
														residualValue: e.target.value,
													}))
												}
												label={uppercaseFR(i.Description)}
											/>
										))}
									</div>
									{form.residualValue !== "2" ? (
										<div className="infoText">
											<p>
												Wenn der FIRMENRADL Restwert geringer ist als der um
												übliche Preisnachlässe verminderte Endpreis des
												Abgabeortes, kommt es zu einer Steuerpflicht i.d.H.v.
												{" "}
												{formatValue({
													value: Number(
														calculator.SteuerpflichtBetrag,
													).toString(),
													groupSeparator: ".",
													decimalSeparator: ",",
													decimalScale: 2,
												})}
												{" "}
												EURO
												.
											</p>
										</div>
									) : (
										<></>
									)}
								</div>

								<div className="checkboxGroupSet">
									<div className="checkboxGroupContainer Tooltip" role="group" aria-label="Vorsteuerabzug">
										<label htmlFor="vorsteuerabzug">
											<input
												name="vorsteuerabzug"
												aria-labelledby="vorsteuerabzug"
												type="checkbox"
												checked={form.vorsteuerabzug}
												onChange={(e) =>
													setForm((form) => ({
														...form,
														vorsteuerabzug: !form.vorsteuerabzug,
													}))
												}
											/>
											Mein Arbeitgeber ist vorsteuerabzugsberechtigt
											{/*Ist das Unternehmen vorsteuerabzugsberechtigt?*/}
										</label>
										<Tooltip>
											Nutze diese Checkbox, wenn dein Arbeitgeber
											vorsteuerabzugsberechtigt ist,
											<br />
											und ihr im Unternehmen den Abzug auch geltend macht.
										</Tooltip>
									</div>

									<div className="checkboxGroupContainer">
										<label htmlFor="pendlerpauschaleActive">
											<input
												name="pendlerpauschaleActive"
												aria-labelledby="pendlerpauschale-active"
												type="checkbox"
												checked={form.pendlerpauschaleActive}
												onChange={(e) =>
													setForm((form) => {
														const pendlerpauschaleValue = form.familienbonusActive && !form.pendlerpauschaleActive ? form.pendlerpauschale : (form.pendlerpauschaleActive ? 0 : form.pendlerpauschale);
														return {
															...form,
															pendlerpauschaleActive: !form.pendlerpauschaleActive,
															pendlerpauschale: pendlerpauschaleValue,
														};
													})
												}
											/>
											Ich beziehe die Pendlerpauschale{" "}
											<span
												className={form.pendlerpauschaleActive ? "" : "hidden"}
											>
												für
												<CurrencyInput
													id="pendlerpauschale"
													name="pendlerpauschale"
													aria-labelledby="pendlerpauschale"
													placeholder="0"
													allowDecimals={false}
													allowNegativeValue={false}
													value={form.pendlerpauschale}
													onValueChange={(value, name, values) =>
														setForm((form) => ({ ...form, pendlerpauschale: values?.float! }))
													}
												/>{" "}
												km pro Strecke.
											</span>
										</label>
									</div>

									<div className="checkboxGroupContainer">
										<label htmlFor="familienbonusActive">
											<input
												name="familienbonusActive"
												aria-labelledby="familienbonus-active"
												type="checkbox"
												checked={form.familienbonusActive}
												onChange={(e) =>
													setForm((form) => ({
														...form,
														familienbonusActive: !form.familienbonusActive,
														fbBis17: 0,
														fbAb18: 0
													}))
												}
											/>
											Ich beziehe einen Familienbonus
										</label>
									</div>

									{form.familienbonusActive && (
										<div>
											<div className="row indent">
												<div className="col">
													<label htmlFor="fbBis17">Kinder bis 17 Jahre</label>
													<CurrencyInput
														id="fbBis17"
														name="fbBis17"
														aria-labelledby="fbBis17"
														placeholder="0"
														maxLength={2}
														allowDecimals={false}
														allowNegativeValue={false}
														defaultValue={form.fbBis17}
														onValueChange={(value, name, values) =>
															setForm((prevForm) => ({ ...prevForm, fbBis17: values?.float ?? 0 }))
														}
													/>
												</div>
												<div className="col">
													<label htmlFor="fbAb18">Kinder ab 18 Jahre</label>
													<CurrencyInput
														id="fbAb18"
														name="fbAb18"
														aria-labelledby="fbBis18"
														placeholder="0"
														maxLength={2}
														allowDecimals={false}
														allowNegativeValue={false}
														defaultValue={form.fbAb18}
														onValueChange={(value, name, values) =>
															setForm((prevForm) => ({ ...prevForm, fbAb18: values?.float ?? 0 }))
														}
													/>
												</div>
											</div>
											{form.fbAb18 + form.fbBis17 < 1 && (
												<div className="row indent">
													<div
														className="col text-center"
														style={{ marginTop: 8 }}
													>
														<small>
															Mindestens 1 Kind muss angegeben werden.
														</small>
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							</div>
						)}

						<div className={`row summary ${internal ? "internal" : ""}`}>
							{internal && (
								<div className="col">
									<TextTransition
										springConfig={presets.wobbly}
										className="textTransition"
										direction={discountDirection}
									>
										{calculator.ErsparnisInProzent}
									</TextTransition>
									<small>Preisvorteil für Mitarbeiter</small>
								</div>
							)}
							<div className="col">
								<TextTransition
									springConfig={presets.wobbly}
									className="textTransition"
									direction={discountDirection}
								>
									{calculator.Restwert}
								</TextTransition>
								<small>
									Restwert (
									<TextTransition
										springConfig={presets.wobbly}
										className="textTransition"
										direction={discountDirection}
										inline
									>
										{calculator.RestwertInProzent}
									</TextTransition>
									)
								</small>
							</div>
							{internal && (
								<div className="col">
									<TextTransition
										springConfig={presets.wobbly}
										className="textTransition"
										direction={discountDirection}
									>
										{calculator.ErsparnisInEuro}
									</TextTransition>
									<small>Ersparnis</small>
								</div>
							)}

							{internal && (
								<div className="col">
									<TextTransition
										springConfig={presets.wobbly}
										className="textTransition"
										direction={priceDirection}
									>
										{calculator.MonatlicheZahlungAG}
									</TextTransition>
									<small>Monatliche Rate vor Gehaltsumwandlung</small>
								</div>
							)}
							<div className="col Tooltip">
								<TextTransition
									springConfig={presets.wobbly}
									className="textTransition"
									direction={priceDirection}
								>
									{calculator.MonatlicheZahlung}
								</TextTransition>
								<small>Monatliche Rate nach Gehaltsumwandlung</small>
								<Tooltip>Monatliche Belastung von deinem Nettogehalt</Tooltip>
							</div>

							<div className="col Tooltip">
								<TextTransition
									springConfig={presets.wobbly}
									className="textTransition"
									direction={priceDirection}
								>
									{calculator.gesamtPreis}
								</TextTransition>

								<small>Gesamtpreis</small>
								<Tooltip>
									Gesamtsumme aus den <br />
									{form.leasingDuration} monatlichen Raten je{" "}
									{calculator.MonatlicheZahlung} + Restwert (
									{calculator.Restwert})
								</Tooltip>
							</div>
						</div>

						{!internal && (
							<div className="ButtonContainer">
								<div className="BubblesContainer mobile-only">
									<div className="Bubbles mobile-only">
										<div className="Bubble Big">
											<TextTransition
												springConfig={presets.wobbly}
												className="textTransition"
												direction={discountDirection}
											>
												{calculator.ErsparnisInEuro}
											</TextTransition>
											<small>sparen</small>
										</div>
									</div>
								</div>

								<a href={getRegisterUrl()} className="button">
									Jetzt bis zu{" "}
									<TextTransition
										springConfig={presets.wobbly}
										className="textTransition"
										direction={discountDirection}
									>
										{calculator.ErsparnisInProzent}
									</TextTransition>
									&ensp;Preisvorteil sichern
								</a>
							</div>
						)}

						<small className="rechner-info">
							Dieser Rechner bietet eine Richtlinie wie die Ersparnis mit
							FIRMENRADL aussehen könnte. Der tatsächliche Betrag hängt von den
							tatsächlichen Bezügen und Abzügen des Arbeitnehmers ab. Um eine
							100&nbsp;% korrekte Auskunft zu erhalten kontaktiere bitte deinen
							Arbeitgeber bzw. die Personalabrechnungsabteilung deines
							Unternehmens.
						</small>
					</div>
				</div>
			</form>
		</>
	);
}

function HeroImage() {
	// get window width and height for image resizing
	let width = window.innerWidth;
	let height = window.innerHeight;

	// get a larger photo for mobile
	if (width < 800) {
		width = width * 2;
	}

	// reducing width and height to a multiple of 64 to achieve less caching and resizing
	width = width - (width % 64);
	height = height - (height % 64);

	let image = null;
	if (window.innerWidth < 800) {
		image = (
			<Image
				src="assets/mood_red_hero_mobile.jpg"
				width={368}
				height={800}
				crop={true}
				mainPicture={true}
				// @ts-expect-error
				alt="Hol dir dein FIRMENRADL!"
				visibleByDefault
				fetchpriority="high"
			/>
		);
	} else {
		image = (
			<Image
				src="assets/mood_red_hero2.jpg"
				width={width}
				height={height}
				crop={false}
				mainPicture={true}
				// @ts-expect-error
				alt="Hol dir dein FIRMENRADL!"
				visibleByDefault
				fetchpriority="high"
			/>
		);
	}

	return image;
}


function capitalizeFirmenradl(str: string): string {
	return str.replace(/firmenradl/gi, 'FIRMENRADL');
}

function Tab({
	id,
	value,
	name,
	label,
	optionChange,
}: {
	id: string | number;
	value: string | number;
	name: string;
	label: string;
	// @ts-ignore
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	optionChange: any;
}) {
	return (
		<label className={id === value ? "checked" : "not-checked"}>
			<input
				type="radio"
				name={name} //"versicherung"
				value={id}
				checked={id === value}
				onChange={optionChange}
			/>{" "}

			{capitalizeFirmenradl(label)}
		</label>
	);
}

export default Rechner;
